/** useRunBaselineEval — start a baseline eval batch and poll it to terminal.
 *
 * start() POSTs /eval/runs then polls GET /eval/runs/{id} every 2s until the
 * batch reaches completed/failed. loadRun() does the same for a run_id that
 * came from history instead of a fresh POST (resuming the poll if it's still
 * in flight). Polling is cleaned up on unmount and on a fresh start()/loadRun().
 * PostgreSQL owns the scorecard and run.compare_url deep-links to the
 * mirrored Langfuse traces.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { EvalRun } from '@/entities/eval';
import { getEvalApi } from './evalClient';

const POLL_INTERVAL_MS = 2000;

export interface UseRunBaselineEvalResult {
  run: EvalRun | null;
  isRunning: boolean;
  error: string | null;
  start: (promptVersionIds: (string | null)[], configVersionIds: (string | null)[]) => Promise<void>;
  loadRun: (runId: string) => Promise<void>;
}

function isTerminal(run: EvalRun): boolean {
  return run.status === 'completed' || run.status === 'failed';
}

export function useRunBaselineEval(agentId: string): UseRunBaselineEvalResult {
  const [run, setRun] = useState<EvalRun | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const pollUntilTerminal = useCallback(
    (runId: string) => {
      timerRef.current = setInterval(() => {
        void (async () => {
          try {
            const latest = await getEvalApi().getRun(agentId, runId);
            setRun(latest);
            if (isTerminal(latest)) {
              clearTimer();
              setIsRunning(false);
            }
          } catch (err) {
            clearTimer();
            setIsRunning(false);
            setError(err instanceof Error ? err.message : 'Failed to poll eval status');
          }
        })();
      }, POLL_INTERVAL_MS);
    },
    [agentId, clearTimer],
  );

  const start = useCallback(
    async (promptVersionIds: (string | null)[], configVersionIds: (string | null)[]) => {
      clearTimer();
      setError(null);
      setRun(null);
      setIsRunning(true);
      let started: EvalRun;
      try {
        started = await getEvalApi().startRun(agentId, {
          prompt_version_ids: promptVersionIds,
          config_version_ids: configVersionIds,
        });
      } catch (err) {
        setIsRunning(false);
        setError(err instanceof Error ? err.message : 'Failed to start eval');
        return;
      }
      setRun(started);
      if (isTerminal(started)) {
        setIsRunning(false);
        return;
      }
      pollUntilTerminal(started.id);
    },
    [agentId, clearTimer, pollUntilTerminal],
  );

  const loadRun = useCallback(
    async (runId: string) => {
      clearTimer();
      setError(null);
      try {
        const latest = await getEvalApi().getRun(agentId, runId);
        setRun(latest);
        if (isTerminal(latest)) {
          setIsRunning(false);
        } else {
          setIsRunning(true);
          pollUntilTerminal(runId);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load eval run');
      }
    },
    [agentId, clearTimer, pollUntilTerminal],
  );

  return { run, isRunning, error, start, loadRun };
}
